import {
  FacetEngineFilterClause,
  FacetEngineQuery,
  FacetEngineResponse,
} from './MarketplaceFacetTypes';

export type RetrievalSortValue = string | number | boolean | null;

export interface RetrievalSort {
  id: string;
  direction: 'asc' | 'desc';
  field?: string;
  valueType: 'relevance' | 'text' | 'number' | 'boolean' | 'date';
  nulls: 'first' | 'last';
  fingerprint: string;
  includeRank: boolean;
}

export interface SourcePosition {
  sortValue: RetrievalSortValue;
  discriminator: string;
  sourceKey: string;
}

export type SourceProjection =
  | { type: 'full' }
  | { type: 'selected'; paths: string[] }
  | {
      type: 'summary';
      fields: string[];
      nested: { field: string; fields: string[] };
    };

export interface SourceQueryDefinition {
  /** Collection containing the authoritative source documents. */
  collection: string;
  scope: { field: string; value: string };
  identity: { discriminatorField: string; sourceKeyField: string };
  population: Array<{ field: string; values: string[] }>;
}

export interface KeywordSourceQuery extends SourceQueryDefinition {
  term: string;
  filters: FacetEngineFilterClause[];
  sort: RetrievalSort;
  limit: number;
  after?: SourcePosition;
  useHighlight?: boolean;
  projection: SourceProjection;
}

export interface RankedSourceRow {
  discriminator: string;
  sourceKey: string;
  document: Record<string, unknown>;
  sortValue: RetrievalSortValue;
  rank?: number;
  matchEvidence?: Array<{ field: string; headline: string }>;
}

export interface SourceQueryResponse {
  rows: RankedSourceRow[];
  totals: { items: number };
  hasMore: boolean;
  highlightTags?: { preTag: string; postTag: string };
}

export interface IKeywordSourceSearchEngine {
  queryKeywordSources(query: KeywordSourceQuery): Promise<SourceQueryResponse>;
  hasKeywordSources(query: KeywordSourceQuery): Promise<boolean>;
}

export interface SemanticRecipeDefinition {
  population: Array<{
    field: string;
    values: string[];
    operator?: 'in' | 'notIn';
  }>;
  preparationFingerprint: string;
  compatibilityFingerprint: string;
  dimensions: number;
}

export interface SemanticSourceQuery extends SourceQueryDefinition {
  /** Vector collection, independent of the document collection; defaults to collection. */
  semanticCollection?: string;
  vector: readonly number[];
  definitions: readonly SemanticRecipeDefinition[];
  dimensions: number;
  threshold: number;
  revision: string;
  filters: FacetEngineFilterClause[];
  sort: RetrievalSort;
  projection: SourceProjection;
  limit: number;
  after?: SourcePosition;
  /** Absolute deadline in epoch milliseconds, not a duration. */
  deadline: number;
  signal?: AbortSignal;
}

export class SourceQueryError extends Error {
  constructor(
    public code:
      | 'unavailable'
      | 'invalid_collator'
      | 'invalid_field'
      | 'invalid_operator'
      | 'query_error'
      | 'internal_error',
    message: string,
  ) {
    super(message);
    this.name = 'SourceQueryError';
  }
}

export interface ISemanticSourceSearchEngine {
  /**
   * Counts distinct source documents across the full eligible semantic match set,
   * excluding each facet's own filter while retaining all other constraints.
   * Result-page limit and position do not restrict counts; facets.limit and
   * facets.after paginate facet values instead.
   */
  querySemanticFacets(
    query: SemanticSourceQuery,
    facets: Pick<FacetEngineQuery, 'facets' | 'limit' | 'after'>,
  ): Promise<FacetEngineResponse>;
  querySemanticSources(
    query: SemanticSourceQuery,
  ): Promise<SourceQueryResponse>;
  scanSemanticSources(
    query: SemanticSourceQuery,
    visit: (page: SourceQueryResponse) => Promise<void>,
  ): Promise<void>;
}
